import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Award,
  Clock,
  DollarSign,
  FileText,
  House,
  MessageCircle,
  Monitor,
  Package,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react';
import logoMark from '../../img/logo-branco.png';
import { prefersReducedMotion, useCountUp } from './hooks';

// Seção "Telas do sistema": mockups ILUSTRATIVOS (dados de exemplo) com abas
// automáticas, cursor simulado, tilt em perspectiva e um celular que acompanha a
// aba ativa. Ref: `ui-ux/landing-flow-vendas-v2.html` (#telas). Estilos em landing.css.

const DUR = 5500;

const TABS = [
  { label: 'Home', Icon: House },
  { label: 'Vendas', Icon: Monitor },
  { label: 'Estoque', Icon: Warehouse },
  { label: 'Ordens de serviço', Icon: Wrench },
  { label: 'Financeiro', Icon: Wallet },
];

const SIDEBAR = [
  { group: 'Principal', items: [{ label: 'Home', s: 0 }, { label: 'Vendas', s: 1 }, { label: 'Orçamentos' }, { label: 'Ordens de Serviço', s: 3 }] },
  { group: 'Cadastros', items: [{ label: 'Clientes' }, { label: 'Produtos', s: 2 }, { label: 'Fornecedores' }] },
  { group: 'Financeiro', items: [{ label: 'Caixas' }, { label: 'Transações', s: 4 }] },
];

const KPIS = [
  { Icon: DollarSign, bg: '#dbeafe', fg: '#2563eb', delta: '↑ 12.4%', to: 48290, money: true, label: 'Vendas hoje' },
  { Icon: Award, bg: '#dcfce7', fg: '#16a34a', delta: '↑ 2 desde ontem', to: 9, label: 'Ordens Hoje (pagas)' },
  { Icon: Package, bg: '#fef3c7', fg: '#b45309', delta: '↑ 8.2%', to: 214, label: 'Total de Produtos Vendidos' },
  { Icon: Clock, bg: '#fee2e2', fg: '#dc2626', delta: '↑ 3 hoje', to: 18, label: 'Ordens de serviço abertas' },
];

const WEEK_BARS = [52, 68, 44, 81, 63, 95, 37];
const WEEK_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const SALES = [
  ['FV-2467', '14/09/2026 09:12', 'R$ 349,80', 'bl', 'Iniciada'],
  ['FV-2466', '13/09/2026 19:40', 'R$ 180,00', 'gr', 'Finalizada'],
  ['FV-2465', '13/09/2026 17:05', 'R$ 412,50', 'gr', 'Finalizada'],
  ['FV-2463', '12/09/2026 20:08', 'R$ 729,90', 'gr', 'Finalizada'],
  ['FV-2461', '11/09/2026 14:32', 'R$ 349,80', 'bl', 'Iniciada'],
  ['FV-2460', '11/09/2026 10:15', 'R$ 58,90', 'gr', 'Finalizada'],
];

const PRODUCTS = [
  { name: 'Filtro de óleo blindado', sku: 'FLT-0012', cat: 'Peças de Motor', price: 'R$ 32,90', qty: 64, w: '100%', pl: 'gr', status: 'Ativo' },
  { name: 'Correia dentada reforçada', sku: 'CRD-0045', cat: 'Peças de Motor', price: 'R$ 79,90', qty: 18, w: '42%', pl: 'gr', status: 'Ativo' },
  { name: 'Jogo de juntas do cabeçote', sku: 'JNT-0078', cat: 'Peças de Motor', price: 'R$ 118,00', qty: 0, w: '6%', low: true, pl: 'am', status: 'Estoque baixo' },
  { name: 'Pneu aro 15 175/65', sku: 'PNU-0150', cat: 'Pneus & Rodas', price: 'R$ 349,90', qty: 32, w: '70%', pl: 'gr', status: 'Ativo' },
  { name: 'Roda liga leve aro 17', sku: 'RDA-0200', cat: 'Pneus & Rodas', price: 'R$ 599,00', qty: 6, w: '18%', pl: 'gr', status: 'Ativo' },
];

const ORDERS = [
  ['#FV-OS-2231', 'Fiat Argo 2022', 'Marcos Vinícius', 'am', 'Não iniciada'],
  ['#FV-OS-2228', 'Chevrolet Onix 2021', 'Ana Luiza Prado', 'bl', 'Em execução'],
  ['#FV-OS-2219', 'VW Gol 2019', 'Rafael Costa', 'gr', 'Finalizada'],
  ['#FV-OS-2205', 'Honda CG 160 2020', 'Marcos Vinícius', 'gr', 'Faturada'],
  ['#FV-OS-2190', 'Chevrolet Onix 2021', 'Ana Luiza Prado', 'rd', 'Cancelada'],
];

const ORDER_STATUS_BARS = [
  ['Não iniciada', '35%', '#f59e0b'],
  ['Em execução', '62%', '#2563eb'],
  ['Finalizada', '80%', '#16a34a'],
  ['Faturada', '55%', '#15803d'],
  ['Cancelada', '14%', '#ef4444'],
];

const CASH_MONTHS = [
  ['Jan', 38, 22],
  ['Fev', 46, 28],
  ['Mar', 34, 36],
  ['Abr', 55, 30],
  ['Mai', 62, 34],
  ['Jun', 70, 32],
];

const style = (vars) => vars;

function KpiValue({ to, money, active, runKey, className }) {
  // Sobe de 0 ao valor ao entrar na aba Home (atraso para acompanhar a animação do card).
  const v = useCountUp(to, { play: active && runKey > 0, duration: 1100, delay: 350, runKey });
  return <div className={className}>{money ? `R$ ${v.toLocaleString('pt-BR')}` : v}</div>;
}

function DBars({ idPrefix }) {
  return (
    <>
      <div className="dbars">
        {WEEK_BARS.map((h, i) => (
          <div className="c" key={`${idPrefix}-${WEEK_DAYS[i]}`}><span className="bar" style={style({ '--h': `${h}%`, '--i': i })} /></div>
        ))}
      </div>
      <div className="dlab">{WEEK_DAYS.map((d) => <span key={d}>{d}</span>)}</div>
    </>
  );
}

function CashChart({ legendSaldo, className }) {
  return (
    <>
      <div className="lg2">
        <span><i style={{ background: 'var(--indigo)' }} />Entradas</span>
        <span><i style={{ background: '#ef4444' }} />Saídas</span>
        <span><i style={{ background: '#f59e0b' }} />{legendSaldo}</span>
      </div>
      <div className="bars">
        <span className="axis" />
        {CASH_MONTHS.map(([m, u, d], i) => (
          <div className="m" key={m}>
            <span className="up" style={style({ '--u': `${u}%`, '--i': i })} />
            <span className="dn" style={style({ '--d': `${d}%`, '--i': i })} />
          </div>
        ))}
      </div>
      <div className="mlab">{CASH_MONTHS.map(([m]) => <span key={m}>{m}</span>)}</div>
      <svg className={className || 'saldo'} viewBox="0 0 100 100" preserveAspectRatio="none">
        <path pathLength="1" vectorEffect="non-scaling-stroke" d="M8 62 C20 58 28 66 41 55 S62 38 75 30 S92 16 92 14" />
      </svg>
    </>
  );
}

export default function ScreensShowcase() {
  const reduce = prefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [prev, setPrev] = useState(-1);
  const [manual, setManual] = useState(false);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [runKey, setRunKey] = useState(0);

  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const winRef = useRef(null);
  const curRef = useRef(null);
  const tabRefs = useRef([]);

  const idxRef = useRef(0);

  const goTo = useCallback((n) => {
    setPrev(idxRef.current);
    idxRef.current = n;
    setIdx(n);
    setRunKey((k) => k + 1);
  }, []);

  // Visibilidade da seção: só roda o ciclo automático enquanto está na tela.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => setVisible(e.isIntersecting)), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Reinicia barra de progresso/animações ao voltar a ficar visível ou ao sair da pausa.
  useEffect(() => {
    if (visible && !paused && !manual) setRunKey((k) => k + 1);
  }, [visible, paused, manual]);

  // Ciclo automático: cursor simulado vai até a próxima aba, clica e troca a tela.
  useEffect(() => {
    if (reduce || manual || paused || !visible) return undefined;
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    const cur = curRef.current;
    const stage = stageRef.current;
    const n = (idx + 1) % TABS.length;
    later(() => {
      const tab = tabRefs.current[n];
      if (!tab || !cur || !stage) {
        goTo(n);
        return;
      }
      const s = stage.getBoundingClientRect();
      const r = tab.getBoundingClientRect();
      cur.classList.add('show');
      cur.style.transform = `translate(${r.left - s.left + r.width / 2 - 4}px,${r.top - s.top + r.height / 2 - 2}px)`;
      later(() => {
        cur.classList.remove('click');
        void cur.getBoundingClientRect();
        cur.classList.add('click');
        later(() => {
          goTo(n);
          later(() => cur.classList.remove('show'), 700);
        }, 180);
      }, 950);
    }, DUR - 1150);
    return () => {
      timers.forEach(clearTimeout);
      if (cur) cur.classList.remove('show');
    };
  }, [idx, manual, paused, visible, reduce, goTo]);

  // Tilt em perspectiva: abre achatado conforme o scroll + inclinação leve com o mouse
  // e parallax dos elementos flutuantes.
  useEffect(() => {
    if (reduce) return undefined;
    const stage = stageRef.current;
    const win = winRef.current;
    if (!stage || !win) return undefined;
    let mx = 0;
    let my = 0;
    let ticking = false;
    let raf = 0;
    const pars = Array.from(stage.querySelectorAll('[data-par]'));
    const frame = () => {
      ticking = false;
      const r = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.75)));
      win.style.setProperty('--rx', `${(16 * (1 - p) + my * -4).toFixed(2)}deg`);
      win.style.setProperty('--ry', `${(mx * 6 + (1 - p) * -4).toFixed(2)}deg`);
      win.style.setProperty('--ty', `${((1 - p) * 40).toFixed(1)}px`);
      const off = r.top + r.height / 2 - vh / 2;
      pars.forEach((el) => el.style.setProperty('--py', `${(off * Number(el.dataset.par)).toFixed(1)}px`));
    };
    const req = () => {
      if (!ticking) {
        ticking = true;
        raf = requestAnimationFrame(frame);
      }
    };
    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      mx = (e.clientX - r.left) / r.width - 0.5;
      my = (e.clientY - r.top) / r.height - 0.5;
      req();
    };
    const onLeave = () => {
      mx = 0;
      my = 0;
      req();
    };
    window.addEventListener('scroll', req, { passive: true });
    window.addEventListener('resize', req);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', onLeave);
    req();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', req);
      window.removeEventListener('resize', req);
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerleave', onLeave);
    };
  }, [reduce]);

  const handleTab = (n) => {
    setManual(true);
    goTo(n);
  };

  const scr = (k) => `scr${idx === k ? ' on' : ''}${prev === k && idx !== k ? ' out' : ''}`;
  const ps = (k) => `ps${idx === k ? ' on' : ''}${prev === k && idx !== k ? ' out' : ''}`;

  return (
    <section className="screens" id="telas">
      <div className="wrap">
        <div className="section-head reveal">
          <p className="eyebrow">Veja por dentro</p>
          <h2>As telas do Flow Vendas, em ação</h2>
          <p>Home, vendas, estoque, ordens de serviço e financeiro no mesmo sistema, direto pelo navegador. Telas ilustrativas, com dados de exemplo.</p>
        </div>
        <div
          ref={wrapRef}
          className={`sc-wrap${paused ? ' paused' : ''}${manual ? ' manual' : ''}`}
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
        >
          <div className="sc-tabs" role="tablist" aria-label="Telas do sistema">
            {TABS.map(({ label, Icon }, k) => (
              <button
                key={label}
                ref={(el) => { tabRefs.current[k] = el; }}
                type="button"
                className={`sc-tab${idx === k ? ' on' : ''}`}
                role="tab"
                aria-selected={idx === k}
                onClick={() => handleTab(k)}
              >
                <Icon className="i" aria-hidden="true" />
                {label}
                <span className="pb" key={`pb-${runKey}`} />
              </button>
            ))}
          </div>

          <div className="stage" ref={stageRef}>
            <div className="fl f1" data-par="-.06" aria-hidden="true">
              <span className="ci" style={{ background: 'var(--wa)' }}><MessageCircle className="i" /></span>Orçamento no WhatsApp
            </div>
            <div className="fl f2" data-par=".08" aria-hidden="true">
              <span className="ci" style={{ background: 'var(--orange)' }}><Wrench className="i" /></span>Ordem de serviço
            </div>

            <div className="win" ref={winRef}>
              <div className="win-bar"><i /><i /><i /><span className="win-url">Flow Vendas</span></div>
              <div className="app">
                <aside className="sb" aria-hidden="true">
                  <div className="lg">
                    <span className="brand-mark"><img className="logo-img" src={logoMark} alt="" style={{ width: 15, height: 15 }} /></span>Flow Vendas
                  </div>
                  {SIDEBAR.map(({ group, items }) => (
                    <React.Fragment key={group}>
                      <small>{group}</small>
                      {items.map(({ label, s }) => (
                        <div key={label} className={`it${s !== undefined && s === idx ? ' on' : ''}`}>{label}</div>
                      ))}
                    </React.Fragment>
                  ))}
                </aside>

                <div className="main">
                  {/* Home */}
                  <div className={scr(0)}>
                    <div className="scr-head">
                      <div><h5>Bem-vindo de volta, Carlos</h5><div className="sub">Aqui está um resumo do seu negócio hoje, 11 de setembro.</div></div>
                    </div>
                    <div className="kpis">
                      {KPIS.map(({ Icon, bg, fg, delta, to, money, label }, i) => (
                        <div className="kp" style={style({ '--i': i })} key={label}>
                          <div className="kp-top">
                            <span className="kp-ic" style={{ background: bg, color: fg }}><Icon size={14} aria-hidden="true" /></span>
                            <span className="kp-d">{delta}</span>
                          </div>
                          <KpiValue className="kp-v" to={to} money={money} active={idx === 0} runKey={runKey} />
                          <div className="kp-l">{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="hrow">
                      <div className="card hch">
                        <div className="hch-h">
                          <div><h6 style={{ margin: 0 }}>Vendas nos últimos 7 dias</h6><div className="sub">Total no período: R$ 312.940</div></div>
                          <span className="seg"><b className="a">7d</b><b>30d</b><b>90d</b></span>
                        </div>
                        <DBars idPrefix="d" />
                      </div>
                      <div className="card hmod">
                        <div className="hmod-h">
                          <span className="kp-ic" style={{ background: '#dbeafe', color: '#2563eb', width: 24, height: 24 }}><FileText size={14} aria-hidden="true" /></span>Orçamentos
                        </div>
                        <div className="ms" style={style({ '--i': 0 })}><span>Valor total</span><b>R$ 118.640</b></div>
                        <div className="ms" style={style({ '--i': 1 })}><span>Aguard. aprov.</span><b>37</b></div>
                        <div className="ms" style={style({ '--i': 2 })}><span>Conversão</span><b style={{ color: '#8b5cf6' }}>23,8%</b></div>
                      </div>
                    </div>
                  </div>

                  {/* Vendas */}
                  <div className={scr(1)}>
                    <div className="scr-head"><div><h5>Vendas</h5><div className="sub">Acompanhe as vendas realizadas</div></div><span className="mbtn">Nova venda</span></div>
                    <div className="tbl" style={style({ '--cols': '1fr 1.3fr 1fr 1fr', '--cols-sm': '1fr 1fr 1fr' })}>
                      <div className="tr th"><span>Número</span><span className="hide-sm">Data</span><span>Valor total</span><span>Status</span></div>
                      {SALES.map(([num, date, total, cls, status], i) => (
                        <div className="tr" style={style({ '--i': i })} key={num}>
                          <b>{num}</b><span className="hide-sm">{date}</span><b>{total}</b>
                          <span><span className={`pl ${cls}`} style={style({ '--i': i })}>{status}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Estoque */}
                  <div className={scr(2)}>
                    <div className="scr-head"><div><h5>Produtos</h5><div className="sub">Controle de estoque e preços</div></div><span className="mbtn">Novo produto</span></div>
                    <div className="tbl" style={style({ '--cols': '2fr 1.2fr .9fr 1.3fr 1fr', '--cols-sm': '2fr 1.2fr 1fr' })}>
                      <div className="tr th"><span>Produto</span><span className="hide-sm">Categoria</span><span>Preço</span><span className="hide-sm">Estoque</span><span>Status</span></div>
                      {PRODUCTS.map((p, i) => (
                        <div className="tr" style={style({ '--i': i })} key={p.sku}>
                          <span><b>{p.name}</b><span className="s2">{p.sku}</span></span>
                          <span className="hide-sm">{p.cat}</span>
                          <b>{p.price}</b>
                          <span className="stk hide-sm"><b>{p.qty}</b><span className="bar"><span className={p.low ? 'low' : undefined} style={style({ '--w': p.w })} /></span></span>
                          <span><span className={`pl ${p.pl}`} style={style({ '--i': i })}>{p.status}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ordens de serviço */}
                  <div className={scr(3)}>
                    <div className="scr-head"><div><h5>Ordens de Serviço</h5><div className="sub">Acompanhe cada trabalho em tempo real</div></div><span className="mbtn">Nova ordem</span></div>
                    <div className="split">
                      <div className="tbl" style={style({ '--cols': '1.4fr 1.2fr 1fr', '--cols-sm': '1.4fr 1fr' })}>
                        <div className="tr th"><span>Ordem</span><span className="hide-sm">Cliente</span><span>Status</span></div>
                        {ORDERS.map(([num, car, customer, cls, status], i) => (
                          <div className="tr" style={style({ '--i': i })} key={num}>
                            <span><b>{num}</b><span className="s2">{car}</span></span>
                            <span className="hide-sm">{customer}</span>
                            <span><span className={`pl ${cls}`} style={style({ '--i': i })}>{status}</span></span>
                          </div>
                        ))}
                      </div>
                      <div className="card hide-sm">
                        <h6>Status das ordens</h6>
                        {ORDER_STATUS_BARS.map(([label, w, c], i) => (
                          <div className="hb" style={style({ '--i': i })} key={label}>
                            <span>{label}</span><div className="t"><span style={style({ '--w': w, '--c': c })} /></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Financeiro */}
                  <div className={scr(4)}>
                    <div className="scr-head"><div><h5>Fluxo de caixa</h5><div className="sub">Entradas, saídas e evolução do saldo mês a mês</div></div><span className="mbtn">Exportar</span></div>
                    <div className="chart"><CashChart legendSaldo="Saldo acumulado" /></div>
                  </div>
                </div>
              </div>
            </div>

            <svg className="sim-cursor" ref={curRef} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 3l15 7-6.5 2L9.5 19z" fill="#14142b" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>

            {/* Celular: acompanha a aba ativa com a versão mobile da mesma tela */}
            <div className="phone" data-par=".12" aria-hidden="true">
              <div className="phone-in">
                <div className={ps(0)}>
                  <h6>Home</h6>
                  <div className="pgrid">
                    {KPIS.map(({ Icon, bg, fg, to, money, label }, i) => (
                      <div className="pc" style={style({ '--i': i })} key={label}>
                        <div className="kp-top">
                          <span className="kp-ic" style={{ background: bg, color: fg, width: 20, height: 20 }}><Icon size={14} /></span>
                        </div>
                        <KpiValue className="kp-v" to={to} money={money} active={idx === 0} runKey={runKey} />
                        <span className="s2" style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pchart" style={{ marginTop: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: '9.5px', color: '#0f172a', marginBottom: 6 }}>Vendas nos últimos 7 dias</div>
                    <DBars idPrefix="p" />
                  </div>
                </div>

                <div className={ps(1)}>
                  <h6>Vendas</h6>
                  {SALES.slice(0, 4).map(([num, date, total, cls, status], i) => (
                    <div className="pc row" style={style({ '--i': i })} key={num}>
                      <div><b>{num}</b><span className="s2">{`${date.slice(0, 5)} ${date.slice(11)}`}</span></div>
                      <div style={{ textAlign: 'right' }}><b>{total}</b><span className={`pl ${cls}`}>{status}</span></div>
                    </div>
                  ))}
                </div>

                <div className={ps(2)}>
                  <h6>Produtos</h6>
                  {[PRODUCTS[0], PRODUCTS[2], PRODUCTS[1], PRODUCTS[3]].map((p, i) => (
                    <div className="pc" style={style({ '--i': i })} key={p.sku}>
                      <div style={{ display: 'grid', gap: 3, justifyItems: 'start' }}>
                        <div style={{ maxWidth: '100%' }}><b>{p.name}</b><span className="s2">{`${p.sku} · ${p.qty} un.`}</span></div>
                        <span className={`pl ${p.pl}`} style={{ alignSelf: 'flex-start' }}>{p.status}</span>
                      </div>
                      <div className="pbar"><span className={p.low ? 'low' : undefined} style={style({ '--w': p.w, '--i': i })} /></div>
                    </div>
                  ))}
                </div>

                <div className={ps(3)}>
                  <h6>Ordens de Serviço</h6>
                  {[ORDERS[1], ORDERS[2], ORDERS[3], ORDERS[0]].map(([num, car, , cls, status], i) => (
                    <div className="pc" style={style({ '--i': i })} key={num}>
                      <div><b>{num}</b><span className="s2">{car}</span></div>
                      <span className={`pl ${cls}`}>{status}</span>
                    </div>
                  ))}
                </div>

                <div className={ps(4)}>
                  <h6>Fluxo de caixa</h6>
                  <div className="pchart"><CashChart legendSaldo="Saldo" /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
